import { useId } from "react";
import { useTranslation } from "react-i18next";

import { activeStates } from "@/lib/states";
import { LANGUAGES } from "@/lib/languages";

/**
 * apps/web :: components/admin :: QuestionFilterBar
 *
 * State + category + locale filters for the admin question list. Parent
 * owns the filter values and passes `onChange` — this component is a
 * pure leaf. Each control is a real `<label for>` + `<select>` pair so
 * screen readers announce the filter purpose correctly (see a11y audit
 * in PR body).
 */
const CATEGORY_ENUM = [
  "RIGHT_OF_WAY",
  "SPEED_LIMITS",
  "SIGNS_AND_SIGNALS",
  "IMPAIRED_DRIVING",
  "PARKING",
  "SHARING_THE_ROAD",
  "SCHOOL_ZONES",
  "LANE_CHANGES",
  "EMERGENCY_VEHICLES",
] as const;

export interface Filters {
  state: string;
  category: string;
  locale: string;
}

export interface QuestionFilterBarProps {
  value: Filters;
  onChange: (next: Filters) => void;
  disabled?: boolean;
}

export default function QuestionFilterBar({
  value,
  onChange,
  disabled = false,
}: QuestionFilterBarProps) {
  const { t } = useTranslation();
  const stateId = useId();
  const categoryId = useId();
  const localeId = useId();
  const cleared = !value.state && !value.category && !value.locale;

  return (
    <div
      data-testid="admin-filter-bar"
      className="flex flex-wrap items-end gap-md rounded-md border border-brand-primary/10 bg-white p-md shadow-sm"
    >
      <div>
        <label
          htmlFor={stateId}
          className="block text-xs font-semibold uppercase tracking-wide text-neutral-600"
        >
          {t("admin.filter.state")}
        </label>
        <select
          id={stateId}
          data-testid="admin-filter-state"
          disabled={disabled}
          value={value.state}
          onChange={(ev) =>
            onChange({ ...value, state: ev.currentTarget.value })
          }
          className="mt-xs rounded-md border border-neutral-300 bg-white px-sm py-xs text-sm text-neutral-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          <option value="">{t("admin.filter.all")}</option>
          {activeStates().map((s) => (
            <option key={s.code} value={s.code}>
              {s.code} — {s.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor={categoryId}
          className="block text-xs font-semibold uppercase tracking-wide text-neutral-600"
        >
          {t("admin.filter.category")}
        </label>
        <select
          id={categoryId}
          data-testid="admin-filter-category"
          disabled={disabled}
          value={value.category}
          onChange={(ev) =>
            onChange({ ...value, category: ev.currentTarget.value })
          }
          className="mt-xs rounded-md border border-neutral-300 bg-white px-sm py-xs text-sm text-neutral-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          <option value="">{t("admin.filter.all")}</option>
          {CATEGORY_ENUM.map((c) => (
            <option key={c} value={c}>
              {t(`admin.category.${c.toLowerCase()}`, {
                defaultValue: c.replaceAll("_", " "),
              })}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor={localeId}
          className="block text-xs font-semibold uppercase tracking-wide text-neutral-600"
        >
          {t("admin.filter.locale")}
        </label>
        <select
          id={localeId}
          data-testid="admin-filter-locale"
          disabled={disabled}
          value={value.locale}
          onChange={(ev) =>
            onChange({ ...value, locale: ev.currentTarget.value })
          }
          className="mt-xs rounded-md border border-neutral-300 bg-white px-sm py-xs text-sm text-neutral-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          <option value="">{t("admin.filter.all")}</option>
          {LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>
              {l.nativeName} ({l.code})
            </option>
          ))}
        </select>
      </div>

      <button
        type="button"
        data-testid="admin-filter-clear"
        disabled={disabled || cleared}
        onClick={() => onChange({ state: "", category: "", locale: "" })}
        className="inline-flex items-center rounded-md border border-brand-primary/40 bg-white px-sm py-xs text-sm font-medium text-brand-primary transition-colors hover:bg-brand-primary/5 disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
      >
        {t("admin.filter.clear")}
      </button>
    </div>
  );
}
